import {inject, Injectable} from '../di';
import {AppInitializeService} from '../app-initialize-service';
import {Logger} from '../logger';
import {ConfigService} from '../config/config-service';
import {TvModuleName} from './tv-module-name';
import {tvModulesConfig} from './tv-modules-config';
import {TvModuleDefinition} from './tv-module-definition';
import {LegacyLogService} from '../logger/legacy-log-service';
import {ProgressBarService} from '../progress-bar/progress-bar-service';
import {TvModule} from './tv-module';

/**
 * Service responsible for loading and initializing Tv Modules
 */
@Injectable()
export class TvModuleService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly legacyLogService = inject(LegacyLogService);
  private readonly configService = inject(ConfigService);
  private readonly progressBarService = inject(ProgressBarService);

  /**
   * List of modules to load unconditionally.
   */
  private readonly initialModules: TvModuleName[] = [
    TvModuleName.Svg,
    TvModuleName.Information,
    TvModuleName.Stat,
    TvModuleName.ViewBill,
    TvModuleName.RemoteControl,
    TvModuleName.VirtualScroll,
    TvModuleName.TvMosaic,
    TvModuleName.Services,
    TvModuleName.Shop,
    TvModuleName.Epg,
    TvModuleName.CustomNotification,
    TvModuleName.Apps,
    TvModuleName.Menu,
    TvModuleName.Welcome,
    TvModuleName.Messages,
    TvModuleName.ScrollContainer,
    TvModuleName.PictureMode,
    TvModuleName.VerticalGallery,
    TvModuleName.YandexStation,
  ];

  /**
   * Statistics about module loading (used in Tests module)
   */
  public readonly moduleStats: Partial<Record<TvModuleName, ModuleLoadStatistics>> = {};

  /**
   * List of loaded and initialized modules
   */
  public readonly initializedModules: {name: TvModuleName; module: TvModule}[] = [];

  constructor() {
    window.Modules = this;
  }

  public async init(): Promise<boolean> {
    try {
      const modulesInitQueue = this.buildModulesInitializationQueue();
      const startTime = Date.now();
      const length = modulesInitQueue.length;

      this.progressBarService.moduleLoadingStart(length);

      this.legacyLogService.add(`MODULES: loading ${length} modules`);

      const moduleNames = modulesInitQueue.map((m) => m.name).join(', ');
      this.logger.debug(`[TvModuleService] Loading ${length} modules: ${moduleNames}`);

      for (const moduleDefinition of modulesInitQueue) {
        await this.loadAndInitModule(moduleDefinition);
        this.progressBarService.moduleLoadingEnd();
      }

      const elapsed = Date.now() - startTime;

      this.legacyLogService.add(`MODULES: all loaded in ${elapsed}ms`);

      const initializedModuleNames = this.initializedModules.map((m) => m.name).join(', ');
      this.logger.debug(
        `[TvModuleService] ${this.initializedModules.length} modules loaded successfully in ${elapsed}ms: ${initializedModuleNames}`,
      );
    } catch (err) {
      this.logger.critical('[TvModuleService] Unable to load modules', err);
      return Promise.resolve(false);
    }

    return Promise.resolve(true);
  }

  /**
   * Load and initialize module
   */
  public async load(moduleName: TvModuleName) {
    const m = this.getModule(moduleName);
    if (m) {
      return m;
    }

    const cfg = this.resolveModuleConfig(moduleName);
    if (!cfg) {
      this.logger.error(`[TvModuleService] Attempt to initialize unknown module ${moduleName}`);
      return Promise.reject(new Error(`Unknown module ${moduleName}`));
    }

    const module = await this.loadAndInitModule(cfg);

    if (!module) {
      return Promise.reject(new Error(`Unable to load and initialize module ${moduleName}`));
    }

    return module;
  }

  /**
   * Get loaded module by name
   */
  public getModule<T extends TvModule>(moduleName: TvModuleName): T | undefined {
    return this.initializedModules.find((m) => m.name === moduleName)?.module as T | undefined;
  }

  /**
   * Check if module was loaded and initialized
   */
  public checkModule(moduleName: TvModuleName): boolean {
    return this.initializedModules.some((m) => m.name === moduleName);
  }

  private async loadAndInitModule(moduleDefinition: TvModuleDefinition) {
    const moduleName = moduleDefinition.name;
    this.moduleStats[moduleName] = {};
    const stat = this.moduleStats[moduleName];
    const loadingStart = Date.now();

    try {
      this.logger.debug(`[TvModuleService] Loading ${moduleName} module...`);
      const module = await moduleDefinition.load();

      stat.load = Date.now() - loadingStart;
      this.legacyLogService.add('MODULES: loaded ' + moduleName);
      // this.logger.debug(
      //   `[TvModuleService] Module ${moduleName} successfully loaded in ${Date.now() - loadingStart} ms`,
      // );

      const initStart = Date.now();
      if (module.init && typeof module.init === 'function') {
        try {
          this.logger.debug(`[TvModuleService] Initializing ${moduleName} module...`);
          await module.init();
        } catch (err) {
          this.logger.error(`[TvModuleService] Failed to initialize ${moduleName} module!`, err);
          this.legacyLogService.add(`MODULES: init ${moduleName} failed`);
          stat.init = false;
          return;
        }
      }

      this.legacyLogService.add(`MODULES: inited ${moduleName}`);
      stat.init = Date.now() - initStart;
      this.initializedModules.push({
        name: moduleName,
        module: module,
      });

      window[moduleDefinition.globalVariable || moduleDefinition.name] = module;

      this.logger.debug(
        `[TvModuleService] Module ${moduleName} successfully loaded and initialized in ${Date.now() - loadingStart} ms`,
      );

      return module;
    } catch (err) {
      stat.load = false;
      this.logger.error(`[TvModuleService] Failed to load ${moduleName} module!`);
      this.legacyLogService.add('MODULES: ERROR loading ' + moduleName + ': ' + err?.toString());
      return;
    }
  }

  private buildModulesInitializationQueue() {
    const config = this.configService.config;
    const configModules: TvModuleName[] = config.modules || [];
    const modulesToInit = [...this.initialModules, ...configModules];
    const queue: TvModuleDefinition[] = [];
    this.resolveModuleDependencies(modulesToInit, queue, []);
    return queue;
  }

  private resolveModuleDependencies(
    modules: TvModuleName[],
    queue: TvModuleDefinition[],
    traversedNodes: TvModuleName[],
  ) {
    const config = this.configService.config;
    const disabledModules = config.tv?.hacks?.disable_modules;

    modules
      .filter((module, index, arr) => arr.indexOf(module) === index)
      .forEach((moduleName) => {
        if (!moduleName) {
          return;
        }
        if (disabledModules?.includes(moduleName)) {
          this.logger.warn(
            `[TvModuleService] Attempt to load module "${moduleName}" was prevented because module was disabled in "config.tv.hacks.disable_modules"`,
          );
          return;
        }
        if (queue.some((moduleCfg) => moduleCfg.name === moduleName)) {
          // already in queue
          // this.legacyLogService.add(`MODULES: duplicate load of module ${moduleName}`);
          return;
        }
        if (traversedNodes.includes(moduleName)) {
          // Dependency loop
          this.logger.error(
            `[TvModuleService] Module dependency loop: ${traversedNodes.join(' -> ')} -> ${moduleName}`,
          );
          return;
        }

        const cfg = this.resolveModuleConfig(moduleName);
        if (!cfg) {
          this.logger.warn(
            `[TvModuleService] Unable to find definition for module "${moduleName}". Skipping that module...`,
          );
          return;
        }

        if (cfg.disabled) {
          this.logger.warn(
            `[TvModuleService] Module "${moduleName}" is disabled by module definition. Skipping that module.`,
          );
          return;
        }

        try {
          if (cfg.condition && !cfg.condition()) {
            this.logger.error(
              `[TvModuleService] Module "${moduleName}" is disabled by predicate. Skipping that module.`,
            );
            return;
          }
        } catch (err) {
          this.logger.error(
            `[TvModuleService] Unable to execute condition predicate for module "${moduleName}". Skipping that module.`,
            err,
          );
          return;
        }

        if (cfg.deps) {
          this.resolveModuleDependencies(cfg.deps, queue, [...traversedNodes, moduleName]);
        }

        queue.push(cfg);
      });
  }

  private resolveModuleConfig(moduleName: TvModuleName) {
    return tvModulesConfig.find((moduleCfg) => moduleCfg.name === moduleName);
  }
}

export interface ModuleLoadStatistics {
  /**
   * Module loading time in ms.
   * false is used when module was failed to load.
   */
  load?: number | false;

  /**
   * Module initialization time in ms.
   * false is used when module was failed to load.
   */
  init?: number | false;
}

declare global {
  interface Window {
    Modules: TvModuleService;
  }
  const Modules: TvModuleService;
}
